import * as React from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  mobileHeaderRecipe,
  type MobileHeaderRecipeProps,
} from '../recipes/mobile.recipe';
import { Button } from './Button';
import { Inline } from './Layout';
import { StatusChip } from './StatusChip';
import type { StatusTone } from './status-tone';
import { Text } from './Text';

export interface MobileHeaderStatus {
  /** "Dispatching", "On scene". */
  label: string;
  tone?: StatusTone;
  /** When the status began; the chip counts up from it. */
  since?: Date | string | number;
}

export interface MobileHeaderProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    MobileHeaderRecipeProps {
  /** Back to where this screen was opened from. No button without it. */
  onBack?: () => void;
  /** The back button's name for a screen reader: "Back to calls". */
  backLabel?: string;
  /** What the screen is. Shown in the middle when there is no status. */
  title?: React.ReactNode;
  /** The live status of the thing on screen, with its timer: a call's. */
  status?: MobileHeaderStatus;
  /** One action on the right: a more menu, a share button. */
  action?: React.ReactNode;
}

/**
 * The header of a screen opened from a list: back on the left, a status chip
 * with its timer in the middle, one action on the right.
 *
 *   <MobileHeader
 *     surface="floating"
 *     onBack={() => navigate('/calls')}
 *     backLabel="Back to calls"
 *     status={{ label: 'En route', tone: 'phase-en-route', since: call.statusChangedAt }}
 *     action={<Button size="icon-lg" pill variant="secondary" aria-label="More">…</Button>}
 *   />
 *
 * The buttons are the Figma's round ones, a wash over glass, so the same
 * header reads over a map and over a page. `floating` is for over a map,
 * where nothing draws a bar; `solid` is a page's, with the notch cleared.
 * The middle column stays centred on the screen whatever sits either side.
 */
export const MobileHeader = React.forwardRef<HTMLElement, MobileHeaderProps>(
  function MobileHeader(
    {
      onBack,
      backLabel = 'Back',
      title,
      status,
      action,
      surface = 'solid',
      className,
      ...props
    },
    ref
  ) {
    return (
      <header
        ref={ref}
        className={cn(mobileHeaderRecipe({ surface }), className)}
        {...props}
      >
        <Inline gap={0} align="center" justify="start">
          {onBack ? (
            <Button
              variant="secondary"
              size="icon-lg"
              pill
              aria-label={backLabel}
              onClick={onBack}
            >
              <ArrowLeft />
            </Button>
          ) : null}
        </Inline>

        {status ? (
          <StatusChip
            label={status.label}
            tone={status.tone}
            since={status.since}
          />
        ) : (
          <Text as="h1" variant="body-strong" truncate>
            {title}
          </Text>
        )}

        <Inline gap={2} align="center" justify="end">
          {action}
        </Inline>
      </header>
    );
  }
);
